import * as Sentry from "@sentry/react";
import { AlertTriangle, RefreshCw, RotateCcw, Home } from "lucide-react";
import { Button } from "@/components/ui/button";

interface FallbackProps {
  error: Error;
  eventId?: string | null;
  resetError: () => void;
}

function FallbackComponent({ error, eventId, resetError }: FallbackProps) {
  return (
    <div
      role="alert"
      className="min-h-screen grid place-items-center bg-background p-6"
    >
      <div className="w-full max-w-md rounded-2xl border bg-card p-8 text-center shadow-lg">
        <div className="mx-auto mb-5 grid size-14 place-items-center rounded-full bg-destructive/10 text-destructive">
          <AlertTriangle className="size-7" />
        </div>

        <h1 className="text-xl font-bold tracking-tight text-foreground">
          Algo deu errado
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          Encontramos um erro inesperado. Nossa equipe já foi avisada. Você pode
          tentar de novo ou recarregar a página.
        </p>

        <div className="mt-6 flex flex-col gap-2">
          <Button onClick={resetError} className="w-full gap-2">
            <RotateCcw className="size-4" />
            Tentar novamente
          </Button>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button
              variant="outline"
              onClick={() => window.location.reload()}
              className="w-full gap-2"
            >
              <RefreshCw className="size-4" />
              Recarregar página
            </Button>
            <Button variant="outline" asChild className="w-full gap-2">
              <a href="/dashboard">
                <Home className="size-4" />
                Voltar ao início
              </a>
            </Button>
          </div>
        </div>

        {/* Em desenvolvimento mostramos o stack completo; em produção só o código
            do erro (event id do Sentry), útil para o suporte rastrear. */}
        {import.meta.env.DEV ? (
          <details className="mt-6 rounded-lg border bg-muted/40 p-3 text-left">
            <summary className="cursor-pointer text-xs font-medium text-muted-foreground">
              Detalhes técnicos
            </summary>
            <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap wrap-break-word text-xs text-foreground/80">
              {error?.stack || error?.message}
            </pre>
          </details>
        ) : (
          eventId && (
            <p className="mt-6 select-all text-xs text-muted-foreground">
              Código do erro: <span className="font-mono">{eventId}</span>
            </p>
          )
        )}
      </div>
    </div>
  );
}

export const ErrorBoundary = Sentry.withErrorBoundary(
  ({ children }: { children: React.ReactNode }) => <>{children}</>,
  {
    fallback: (props) => (
      <FallbackComponent
        error={props.error as Error}
        eventId={props.eventId}
        resetError={props.resetError}
      />
    ),
    showDialog: false,
  }
);
